import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Localization, Core, Typography, useNotification } from 'connex-cds';
import { DataDenseGrid, DynamicTimePeriodMenu } from '@cai/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Alert, Button, IconButton, TextField } from '@mui/material';
import { useParams } from 'react-router-dom';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import dayjs from 'dayjs';
import { supportedLocales } from '../../../../../i18n/common';
import { useAllDateOptions } from '../../../../../common/useAllDateOptions';
import {
  useVehicleForLoadAssignments,
  useListScheduledLoadAssignments,
  useCreateTicketForLoad,
  filterDisplayableAssignments,
  assignmentStatusCode,
  isPendingStatusCode,
  VEHICLE_ID_STORAGE_KEY,
} from '../../../../../query-hooks/load-assignments';
import { useGetCompanyAdmin } from '../../../../../query-hooks/company';
import { TicketListFooter } from '../ticket-list/components/TableFooter';
import { useMonitorTimeZone } from '../useMonitorTimeZone';
import useThemeConfig from '../useThemeConfig';
import { useTableColumns } from './config';
import { AssignmentDetailsModal } from './AssignmentDetailsModal';
import { CreateTicketModal } from './CreateTicketModal';
import { doesDateFilterIncludeToday, isScheduledForToday, useActiveLoadAssignment } from './active-load-storage';
import * as S from './styles';

const componentAction = ['prev', 'next'];

const LoadAssignmentList = () => {
  const translateMessage = Localization.useTranslateMessage();
  const notify = useNotification();
  const { userLocale, timeZone } = Localization.useLocalizationContext();
  const { entityRef } = useParams();
  const dateOptions = useAllDateOptions();

  // This view renders outside MobileTicket, so it applies the entity calendar itself.
  useMonitorTimeZone();
  const companyQuery = useGetCompanyAdmin();
  const companyTimeZone = companyQuery?.data?.dateTimeSettings?.timeZone;
  const isCompanyTimeZoneResolved = Boolean(!companyQuery?.isLoading && companyTimeZone);

  const [vehicleId, setVehicleId] = useState(() => localStorage.getItem(VEHICLE_ID_STORAGE_KEY) || '');
  const [vehicleDraft, setVehicleDraft] = useState(vehicleId);
  const [editingVehicle, setEditingVehicle] = useState(!vehicleId);
  const [queryParams, setQueryParams] = useState({ dateOption: 'Today' });
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [createTicketAssignment, setCreateTicketAssignment] = useState(null);

  const vehicleQuery = useVehicleForLoadAssignments(vehicleId);
  const vehicleRef = vehicleQuery?.data?.vehicleRef;
  const driverName = vehicleQuery?.data?.driverName;
  const isInvalidVehicle = Boolean(vehicleId) && vehicleQuery?.isSuccess && vehicleQuery?.data?.found === false;
  const isDriverMismatch =
    Boolean(vehicleId) &&
    vehicleQuery?.isSuccess &&
    vehicleQuery?.data?.found === true &&
    vehicleQuery?.data?.matchesLoggedInDriver !== true;

  const assignmentsQuery = useListScheduledLoadAssignments(isDriverMismatch ? null : vehicleRef, queryParams);
  const createTicketMutation = useCreateTicketForLoad();

  useEffect(() => {
    setVehicleDraft(vehicleId);
  }, [vehicleId]);

  const setDateTypeFilter = (option, date) => {
    const nextParams = {};
    if (dayjs(date).isValid() && componentAction.includes(option)) {
      const startDateTimeStamp = new Date(date).setHours(0, 0, 0);
      const startDate = new Date(startDateTimeStamp).toISOString();

      const endDateTimestamp = new Date(date).setHours(23, 59, 59);
      const endDate = new Date(endDateTimestamp).toISOString();

      nextParams.startDate = startDate;
      nextParams.endDate = endDate;
      setQueryParams(nextParams);
    } else {
      nextParams.dateOption = option;
      setQueryParams(nextParams);
    }
  };

  const handleSaveVehicle = useCallback(() => {
    const nextVehicleId = (vehicleDraft || '').trim();
    if (!nextVehicleId) {
      setEditingVehicle(true);
      return;
    }
    localStorage.setItem(VEHICLE_ID_STORAGE_KEY, nextVehicleId);
    setVehicleId(nextVehicleId);
    setEditingVehicle(false);
    // Same ID is a no-op for the query key — force re-resolve so a prior miss can become a hit.
    void vehicleQuery.refreshVehicle?.(nextVehicleId);
  }, [vehicleDraft, vehicleQuery.refreshVehicle]);

  const handleView = useCallback(assignment => {
    setSelectedAssignment(assignment);
    setDetailsOpen(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailsOpen(false);
    setSelectedAssignment(null);
  }, []);

  const handleOpenCreateTicket = useCallback(assignment => {
    setCreateTicketAssignment(assignment);
  }, []);

  const handleCloseCreateTicket = useCallback(() => {
    setCreateTicketAssignment(null);
  }, []);

  const refreshAssignments = useCallback(() => {
    assignmentsQuery.refetch();
  }, [assignmentsQuery.refetch]);

  const displayableAssignments = useMemo(
    () => filterDisplayableAssignments(assignmentsQuery?.data),
    [assignmentsQuery?.data]
  );

  const visibleLoadRefs = useMemo(
    () => displayableAssignments?.map?.(assignment => assignment?.scheduledLoadRef)?.filter?.(Boolean) ?? [],
    [displayableAssignments]
  );

  const dateFilterIncludesToday = useMemo(
    () => doesDateFilterIncludeToday(queryParams, companyTimeZone),
    [queryParams, companyTimeZone]
  );

  const { activeScheduledLoadRef, hasActiveLoad, isStaleActive, activate, deactivate } = useActiveLoadAssignment({
    entityRef,
    vehicleId,
    timeZone: companyTimeZone,
    visibleLoadRefs,
    refreshToken: assignmentsQuery?.dataUpdatedAt,
    enabled: isCompanyTimeZoneResolved,
  });

  const rows = useMemo(
    () =>
      displayableAssignments?.map?.((assignment, index) => {
        const scheduledLoadRef = assignment?.scheduledLoadRef;
        const isActive = scheduledLoadRef === activeScheduledLoadRef;
        const statusCode = assignmentStatusCode(assignment);
        return {
          index,
          id: scheduledLoadRef || `${assignment?.orderRef}-${assignment?.loadNumber}-${index}`,
          scheduledLoadRef,
          orderRef: assignment?.orderRef,
          scheduledYardTime: assignment?.scheduledYardTime,
          orderId: assignment?.orderId,
          loadNumber: assignment?.loadNumber,
          statusCode,
          productName: assignment?.primaryProduct?.name,
          customerName: assignment?.customerName,
          vehicleId: assignment?.vehicleId || vehicleId,
          vehicleRef: assignment?.vehicleRef || vehicleRef,
          driverRef: assignment?.driverRef || vehicleQuery?.data?.driverRef,
          locationRef: vehicleQuery?.data?.locationRef,
          carrierRef: vehicleQuery?.data?.carrierRef,
          primaryProduct: assignment?.primaryProduct,
          supplierStatus: assignment?.supplierStatus,
          isActive,
          canActivate:
            Boolean(scheduledLoadRef) &&
            isCompanyTimeZoneResolved &&
            !isActive &&
            !hasActiveLoad &&
            isPendingStatusCode(statusCode) &&
            isScheduledForToday(assignment?.scheduledYardTime, companyTimeZone),
        };
      }) ?? [],
    [
      displayableAssignments,
      vehicleId,
      vehicleRef,
      vehicleQuery?.data?.driverRef,
      vehicleQuery?.data?.locationRef,
      vehicleQuery?.data?.carrierRef,
      activeScheduledLoadRef,
      hasActiveLoad,
      companyTimeZone,
      isCompanyTimeZoneResolved,
    ]
  );

  const handleSubmitCreateTicket = useCallback(
    async ({ quantity, ticketNumber }) => {
      const assignment = createTicketAssignment;
      const productRef = assignment?.primaryProduct?.productRef;
      if (!assignment?.orderRef || assignment?.loadNumber == null || !productRef) {
        notify?.error?.(translateMessage('loadAssignment_createTicketError'));
        return;
      }

      try {
        const ticket = await createTicketMutation.mutateAsync({
          orderRef: assignment?.orderRef,
          loadNumber: assignment?.loadNumber,
          payload: {
            ...(ticketNumber ? { ticketNumber } : {}),
            vehicleRef: assignment?.vehicleRef,
            driverRef: assignment?.driverRef,
            locationRef: assignment?.locationRef,
            carrierRef: assignment?.carrierRef,
            quantities: [{ productRef, quantity }],
          },
        });

        if (!ticket?.crn && !ticket?.id) {
          throw new Error(translateMessage('loadAssignment_createTicketError'));
        }

        setCreateTicketAssignment(null);
        notify?.success?.(translateMessage('loadAssignment_createTicketSuccess'));
        deactivate?.(assignment?.scheduledLoadRef);
        await assignmentsQuery.refetch?.();
      } catch (error) {
        notify?.error?.(error?.message || translateMessage('loadAssignment_createTicketError'));
      }
    },
    [createTicketAssignment, createTicketMutation, deactivate, assignmentsQuery, notify, translateMessage]
  );

  const columns = useTableColumns({
    onView: handleView,
    onActivate: activate,
    onDeactivate: deactivate,
    onCreateTicket: handleOpenCreateTicket,
  });

  const isLoadingOrRefetching = useMemo(
    () =>
      vehicleQuery?.isLoading ||
      vehicleQuery?.isFetching ||
      assignmentsQuery?.isLoading ||
      assignmentsQuery?.isFetching ||
      assignmentsQuery?.isRefetching,
    [
      vehicleQuery?.isLoading,
      vehicleQuery?.isFetching,
      assignmentsQuery?.isLoading,
      assignmentsQuery?.isFetching,
      assignmentsQuery?.isRefetching,
    ]
  );

  const footerSlotProps = useMemo(
    () => ({
      refetch: refreshAssignments,
      retrieved: rows?.length,
      isLoadingOrRefetching,
    }),
    [refreshAssignments, rows?.length, isLoadingOrRefetching]
  );

  const showEmptyState =
    Boolean(vehicleId) && !isInvalidVehicle && !isDriverMismatch && !isLoadingOrRefetching && rows?.length === 0;

  return (
    <S.FormStyle>
      <S.VehicleBarStyle>
        <S.VehicleIdWrap>
          {editingVehicle ? (
            <>
              <TextField
                size="small"
                label={translateMessage('loadAssignment_vehicleId')}
                value={vehicleDraft}
                onChange={event => setVehicleDraft(event?.target?.value)}
                onKeyDown={event => {
                  if (event?.key === 'Enter') {
                    handleSaveVehicle();
                  }
                }}
              />
              <IconButton
                color="primary"
                onClick={handleSaveVehicle}
                aria-label={translateMessage('loadAssignment_saveVehicle')}
              >
                <CheckOutlinedIcon />
              </IconButton>
            </>
          ) : (
            <>
              <Typography.Subtitle>
                {translateMessage('loadAssignment_vehicleId')} <strong>{vehicleId}</strong>
              </Typography.Subtitle>
              <IconButton
                color="primary"
                size="small"
                onClick={() => setEditingVehicle(true)}
                aria-label={translateMessage('loadAssignment_editVehicle')}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </S.VehicleIdWrap>
        {driverName && !isInvalidVehicle && !isDriverMismatch ? (
          <S.DriverNameStyle>
            <Typography.Subtitle>
              {translateMessage('loadAssignment_driver')} <strong>{driverName}</strong>
            </Typography.Subtitle>
          </S.DriverNameStyle>
        ) : null}
      </S.VehicleBarStyle>

      <S.TimePickerStyle>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={supportedLocales?.[userLocale]?.name}>
          <S.RightWrap>
            <DynamicTimePeriodMenu
              key="load-assignments-date-range-picker"
              format={supportedLocales?.[userLocale]?.formats?.L ?? 'MM/DD/YYYY'}
              timezone={timeZone}
              options={dateOptions}
              callbackFn={setDateTypeFilter}
              disabled={isLoadingOrRefetching || !vehicleId || isInvalidVehicle || isDriverMismatch}
            />
          </S.RightWrap>
        </LocalizationProvider>
      </S.TimePickerStyle>

      {isStaleActive && dateFilterIncludesToday && !isLoadingOrRefetching ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => deactivate()}>
              {translateMessage('loadAssignment_deactivate')}
            </Button>
          }
        >
          {translateMessage('loadAssignment_staleActive')}
        </Alert>
      ) : null}

      {!vehicleId ? (
        <S.EmptyStateStyle>
          <Typography.H3>
            <Localization.Translate stringId="loadAssignment_enterVehicle" />
          </Typography.H3>
        </S.EmptyStateStyle>
      ) : isInvalidVehicle ? (
        <S.EmptyStateStyle>
          <Typography.H3>
            <Localization.Translate stringId="loadAssignment_invalidVehicleId" />
          </Typography.H3>
        </S.EmptyStateStyle>
      ) : isDriverMismatch ? (
        <S.EmptyStateStyle>
          <Typography.H3>
            <Localization.Translate stringId="loadAssignment_vehicleDriverMismatch" />
          </Typography.H3>
        </S.EmptyStateStyle>
      ) : showEmptyState ? (
        <S.EmptyStateStyle>
          <Typography.H3>
            <Localization.Translate stringId="loadAssignment_noAssignments" />
          </Typography.H3>
          <Core.Button
            type="primary"
            stringId="loadAssignment_requestLoad"
            onClick={refreshAssignments}
            data-testid="load-assignment-request-load"
          />
        </S.EmptyStateStyle>
      ) : (
        <S.GridStyle>
          <DataDenseGrid
            variant="base"
            id="load-assignments-list-view"
            columns={columns}
            loading={vehicleQuery?.isLoading || assignmentsQuery?.isLoading}
            rows={rows}
            withRefreshDataButton={false}
            slots={{ footer: TicketListFooter }}
            slotProps={{ footer: footerSlotProps }}
          />
        </S.GridStyle>
      )}

      <AssignmentDetailsModal open={detailsOpen} assignment={selectedAssignment} onClose={handleCloseDetails} />
      <CreateTicketModal
        open={Boolean(createTicketAssignment)}
        assignment={createTicketAssignment}
        isSubmitting={Boolean(createTicketMutation?.isLoading)}
        onClose={handleCloseCreateTicket}
        onSubmit={handleSubmitCreateTicket}
      />
    </S.FormStyle>
  );
};

export default useThemeConfig(LoadAssignmentList);
